import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { API_BASE } from '../api/client.js';
import { eventsApi } from '../api/events.js';
import { Button } from '../components/Button.js';
import { Card } from '../components/Card.js';
import { ErrorState } from '../components/ErrorState.js';
import { EventCalendar } from '../components/EventCalendar.js';
import { EventRequirementsPanel } from '../components/EventRequirementsPanel.js';
import { EventStatsPanel } from '../components/EventStatsPanel.js';
import { Loading } from '../components/Loading.js';
import { MilestoneForm } from '../components/MilestoneForm.js';
import { PrepPlanPreview } from '../components/PrepPlanPreview.js';
import { Tag } from '../components/Tag.js';
import { useEventTimelineQuery, useMyTeamsQuery } from '../hooks/queries.js';
import { formatRelative } from '../lib/format.js';
import { feedSourceLabel } from '../lib/labels.js';
import { qk } from '../lib/queryKeys.js';
import { getAccessToken, useSession } from '../store/session.js';
import { toast } from '../store/toast.js';

/**
 * 赛事详情页（F4）：竞赛日历（里程碑落月历）+ 赛事动态列表 + 生成备赛规划。
 * - 任意登录用户：看日历与动态、加入赛事（幂等）；
 * - 该赛事队伍的成员（已成队）：「生成备赛规划」把 T85 倒推出的任务落到项目看板；
 * - organizer/admin：加里程碑、切换赛事状态。
 */
export function EventDetailPage() {
  const { code } = useParams<{ code: string }>();
  const eventCode = code ?? '';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useSession((state) => state.user);
  const isOrganizer = user?.role === 'organizer' || user?.role === 'admin';

  const timelineQuery = useEventTimelineQuery(eventCode);
  const teamsQuery = useMyTeamsQuery();
  const myTeam = (teamsQuery.data?.teams ?? []).find((team) => team.eventCode === eventCode) ?? null;
  const canGenerate = myTeam !== null && typeof myTeam.projectId === 'string' && myTeam.projectId.length > 0;

  const joinMutation = useMutation({
    mutationFn: () => eventsApi.join(eventCode),
    onSuccess: () => {
      toast.success('已加入赛事', eventCode);
      void queryClient.invalidateQueries({ queryKey: qk.eventTimeline(eventCode) });
      void queryClient.invalidateQueries({ queryKey: qk.events() });
    },
  });

  const addMilestone = useMutation({
    mutationFn: (payload: Parameters<typeof eventsApi.addMilestone>[1]) =>
      eventsApi.addMilestone(eventCode, payload),
    onSuccess: () => {
      toast.success('里程碑已添加', eventCode);
      void queryClient.invalidateQueries({ queryKey: qk.eventTimeline(eventCode) });
      void queryClient.invalidateQueries({ queryKey: qk.events() });
    },
  });

  const closeEvent = useMutation({
    mutationFn: () => eventsApi.update(eventCode, { status: 'closed' }),
    onSuccess: () => {
      toast.success('赛事已关闭', eventCode);
      void queryClient.invalidateQueries({ queryKey: qk.eventTimeline(eventCode) });
      void queryClient.invalidateQueries({ queryKey: qk.events() });
    },
  });

  const prepTimeline = useMutation({
    mutationFn: () => eventsApi.prepTimeline(myTeam?.projectId ?? ''),
    onSuccess: (data) => {
      const created = data.generated.length;
      if (created > 0) {
        toast.success(`已生成 ${created} 条备赛任务`, '已写入项目看板，负责人默认为队长');
      } else {
        toast.info('没有新任务', `全部 ${data.skipped} 条已存在（重复生成会自动跳过）`);
      }
      void queryClient.invalidateQueries({ queryKey: qk.myTasks() });
      if (myTeam?.projectId) {
        void queryClient.invalidateQueries({ queryKey: qk.projectTasks(myTeam.projectId) });
        navigate(`/tasks?project=${encodeURIComponent(myTeam.projectId)}`);
      }
    },
  });

  /** 导出 .ics（扩展④）：带 Bearer 的 fetch → Blob 另存，可导入 Google/Apple/Outlook 日历 */
  const downloadIcs = (): void => {
    const token = getAccessToken();
    fetch(`${API_BASE}${eventsApi.calendarIcsPath(eventCode)}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(async (response) => {
        if (!response.ok) throw new Error(`导出失败（HTTP ${response.status}）`);
        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = objectUrl;
        anchor.download = `crossmatch-${eventCode}.ics`;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(objectUrl);
        toast.success('日历已导出', '双击 .ics 文件即可导入系统日历；重复导入不会重复建事件');
      })
      .catch((error: unknown) => {
        toast.error('导出日历失败', error instanceof Error ? error.message : '请稍后重试');
      });
  };

  if (timelineQuery.isPending) {
    return (
      <div className="stack">
        <div className="page-head">
          <h1 className="page-title">赛事详情</h1>
        </div>
        <Loading label="正在读取赛事时间轴…" rows={4} />
      </div>
    );
  }

  if (timelineQuery.isError) {
    return (
      <div className="stack">
        <div className="page-head">
          <h1 className="page-title">赛事详情</h1>
        </div>
        <ErrorState error={timelineQuery.error} title="读取赛事失败" onRetry={() => void timelineQuery.refetch()} />
      </div>
    );
  }

  const { event, milestones, feeds } = timelineQuery.data;

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1 className="page-title">{event.name}</h1>
          <p className="page-sub">
            赛事码 {event.code}
            {event.season ? ` · ${event.season} 赛季` : ''}
            {typeof event.memberCount === 'number' ? ` · ${event.memberCount} 人已加入` : ''}
          </p>
        </div>
        <div className="row">
          <Tag tone={event.status === 'closed' ? 'muted' : 'ok'}>{event.status === 'closed' ? '已关闭' : '开放中'}</Tag>
          <Button
            variant="secondary"
            size="sm"
            loading={joinMutation.isPending}
            disabled={event.status === 'closed'}
            onClick={() => joinMutation.mutate()}
          >
            加入赛事
          </Button>
        </div>
      </div>

      <Card
        title="赛事动态"
        subtitle={`${feeds.length} 条（来自各校官网与赛事通知的同步条目）`}
      >
        {feeds.length === 0 ? (
          <p className="muted small">暂无动态。动态由系统按赛事关键词从公开来源同步，命中「挑战杯」等全国赛事会自动收录。</p>
        ) : (
          <div className="stack-sm">
            {feeds.map((feed) => (
              <div className="row-tight" key={feed.id}>
                <Tag tone="muted">{feedSourceLabel(feed.source)}</Tag>
                {feed.url.length > 0 ? (
                  <a href={feed.url} target="_blank" rel="noreferrer" className="small wrap-anywhere">
                    {feed.title}
                  </a>
                ) : (
                  <span className="small wrap-anywhere">{feed.title}</span>
                )}
                <span className="muted small right">{formatRelative(feed.publishedAt)}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card
        title="备赛规划"
        subtitle="以赛事里程碑为锚倒推备赛任务，直接写入项目看板。"
        actions={
          <Button
            variant="primary"
            loading={prepTimeline.isPending}
            disabled={!canGenerate}
            title={canGenerate ? undefined : '需要先在本赛事下组建队伍并确认成队'}
            onClick={() => prepTimeline.mutate()}
          >
            生成备赛规划
          </Button>
        }
      >
        {!canGenerate ? (
          <p className="field-hint">
            还没有本赛事下已确认的队伍。先到<Link to="/team"> 我的队伍 </Link>组队（赛事码 {event.code}）并确认成队。
          </p>
        ) : (
          <p className="field-hint">队伍「{myTeam.name}」已确认，点右上角按钮按里程碑倒推备赛任务。</p>
        )}
        <PrepPlanPreview milestones={milestones} />
      </Card>

      <Card
        title="竞赛日历"
        subtitle="节点落在月历里：逾期红、7 天内橙；可导出 .ics 订阅系统日历。"
        actions={
          <Button variant="secondary" size="sm" onClick={downloadIcs}>
            导出 .ics
          </Button>
        }
      >
        <EventCalendar milestones={milestones} />
      </Card>

      {isOrganizer && event.status !== 'closed' ? (
        <Card
          title="维护里程碑"
          subtitle="里程碑是备赛倒推与倒计时的锚点。"
          actions={
            <Button variant="ghost" size="sm" loading={closeEvent.isPending} onClick={() => closeEvent.mutate()}>
              关闭赛事
            </Button>
          }
        >
          <MilestoneForm onSubmit={(payload) => addMilestone.mutate(payload)} submitting={addMilestone.isPending} />
        </Card>
      ) : null}

      {isOrganizer && event.status !== 'closed' ? (
        <Card
          title="典型技能需求"
          subtitle="需求集决定各队伍「缺口雷达」的计算基准。"
        >
          <EventRequirementsPanel eventCode={event.code} />
        </Card>
      ) : null}

      {isOrganizer ? (
        <Card
          title="组织方聚合看板"
          subtitle="匿名进度视图：只出计数不出明细，n<5 一律「<5」（k-匿名）。"
        >
          <EventStatsPanel eventCode={event.code} />
          <div className="row">
            <Link to={`/events/${event.code}/screen`} className="btn btn-primary">
              打开数据大屏
            </Link>
          </div>
        </Card>
      ) : null}
    </div>
  );
}
